/**
 * Языковые способности по мотивам батареи LLAMA (Meara, 2005) и
 * подход «способности × метод обучения» (ATI, Aptitude-Treatment Interaction).
 *
 * Три коротких теста онбординга дают три оценки 0–1:
 *   memory  — LLAMA B: ассоциативная память (символ ↔ выдуманное слово);
 *   sound   — LLAMA D/E: связь звука и написания (услышал → выбери запись);
 *   grammar — LLAMA F: вывод грамматики из примеров на выдуманном языке.
 *
 * По ним определяется когнитивный профиль и стартовый план урока:
 *   ANALYTIC      — сильная грамматика → правило первым (дедуктивно);
 *   MNEMONIC      — сильные память/звук → история первой (индуктивно);
 *   HIGH_APTITUDE — всё высокое → по сильнейшей стороне, обычный темп;
 *   LOW_APTITUDE  — всё невысокое или ровно-среднее → правило первым,
 *                   маленькими шагами и с озвучкой («ведомый» режим).
 *
 * Способности — только старт: каждые 3 урока план тихо пересматривается
 * по фактической точности (`reevaluate`).
 *
 * Содержимое тестов фиксировано (не генерируется): одинаковое для всех,
 * мгновенное и не зависит от сети и модели.
 */

import type { StrategyWeights } from './diagnostic';
import type { Strategy } from './types';

/* ── Содержимое тестов ───────────────────────────────────────────── */

/** LLAMA B: абстрактный символ (id глифа на клиенте) и выдуманное слово. */
export interface MemoryPair {
  readonly glyph: string;
  readonly word: string;
}

export const MEMORY_PAIRS: readonly MemoryPair[] = [
  { glyph: 'spiral', word: 'tavo' },
  { glyph: 'fork', word: 'kemi' },
  { glyph: 'wave', word: 'rulpa' },
  { glyph: 'cross', word: 'zesh' },
  { glyph: 'arch', word: 'bonak' },
  { glyph: 'zigzag', word: 'fiwu' },
];

/** Сколько секунд показываются пары перед проверкой. */
export const MEMORY_STUDY_SECONDS = 45;

/** LLAMA D/E: слово звучит (синтез речи), нужно выбрать написание. */
export interface SoundItem {
  readonly id: string;
  /** Что произносится. */
  readonly spoken: string;
  /** Варианты написания; верный — `spoken`. */
  readonly options: readonly string[];
}

export const SOUND_ITEMS: readonly SoundItem[] = [
  { id: 's1', spoken: 'glorvin', options: ['glorven', 'glorvin', 'klorvin', 'glarvin'] },
  { id: 's2', spoken: 'teshamo', options: ['tesamo', 'teshano', 'teshamo', 'tashemo'] },
  { id: 's3', spoken: 'brindel', options: ['brindel', 'prindel', 'brendel', 'brindle'] },
  { id: 's4', spoken: 'nookara', options: ['nukara', 'nookara', 'nokara', 'nookaro'] },
  { id: 's5', spoken: 'veltishu', options: ['weltishu', 'veltisu', 'veldishu', 'veltishu'] },
];

/** LLAMA F: четыре примера с переводом → выбрать верное пятое предложение. */
export interface GrammarItem {
  readonly id: string;
  readonly examples: readonly { readonly fictional: string; readonly ru: string }[];
  /** Что нужно сказать на выдуманном языке. */
  readonly targetRu: string;
  readonly options: readonly string[];
  readonly answerIndex: number;
  /** Какое правило работало — показывается после ответа. */
  readonly ruleRu: string;
}

export const GRAMMAR_ITEMS: readonly GrammarItem[] = [
  {
    id: 'g1',
    examples: [
      { fictional: 'Mira dun.', ru: 'Девочка ест.' },
      { fictional: 'Mira dunka.', ru: 'Девочка ела.' },
      { fictional: 'Tolo vesh.', ru: 'Собака бежит.' },
      { fictional: 'Toloi vesh.', ru: 'Собаки бегут.' },
    ],
    targetRu: 'Собаки бежали.',
    options: ['Tolo veshka.', 'Toloi veshka.', 'Toloi vesh.', 'Veshka toloi.'],
    answerIndex: 1,
    ruleRu: '-ka у глагола — прошедшее время, -i у существительного — множественное число.',
  },
  {
    id: 'g2',
    examples: [
      { fictional: 'Ana kiton sela.', ru: 'Анна видит кошку.' },
      { fictional: 'Bor kiton tima.', ru: 'Бор кормит кошку.' },
      { fictional: 'Ana boron sela.', ru: 'Анна видит Бора.' },
      { fictional: 'Kit anan tima.', ru: 'Кошка кормит Анну.' },
    ],
    targetRu: 'Бор видит Анну.',
    options: ['Bor ana sela.', 'Boron ana sela.', 'Bor anan sela.', 'Bor sela anan.'],
    answerIndex: 2,
    ruleRu: 'Глагол стоит в конце, а тот, на кого направлено действие, получает -n.',
  },
  {
    id: 'g3',
    examples: [
      { fictional: 'Lo mesa.', ru: 'Он поёт.' },
      { fictional: 'Lo mesa vu.', ru: 'Он не поёт.' },
      { fictional: 'Ka tari.', ru: 'Она спит.' },
      { fictional: 'Ka tari vu.', ru: 'Она не спит.' },
    ],
    targetRu: 'Она не поёт.',
    options: ['Ka vu mesa.', 'Vu ka mesa.', 'Ka mesa vu.', 'Lo mesa vu.'],
    answerIndex: 2,
    ruleRu: 'Отрицание vu ставится после глагола; ka — она, lo — он.',
  },
  {
    id: 'g4',
    examples: [
      { fictional: 'dom raka', ru: 'большой дом' },
      { fictional: 'domi rakai', ru: 'большие дома' },
      { fictional: 'pel suni', ru: 'тихая река' },
      { fictional: 'peli sunii', ru: 'тихие реки' },
    ],
    targetRu: 'тихие дома',
    options: ['domi suni', 'dom sunii', 'sunii domi', 'domi sunii'],
    answerIndex: 3,
    ruleRu: 'Прилагательное идёт после существительного, и множественное -i получают оба.',
  },
];

/* ── Оценки и профиль ────────────────────────────────────────────── */

export interface AptitudeScores {
  /** LLAMA B, 0–1. */
  readonly memory: number;
  /** LLAMA D/E, 0–1; `null` — синтеза речи нет, тест пропущен. */
  readonly sound: number | null;
  /** LLAMA F, 0–1. */
  readonly grammar: number;
}

export const COGNITIVE_PROFILES = ['ANALYTIC', 'MNEMONIC', 'HIGH_APTITUDE', 'LOW_APTITUDE'] as const;
export type CognitiveProfile = (typeof COGNITIVE_PROFILES)[number];

/** Доля верных ответов, 0–1; пустой тест — 0. */
export function scoreOf(correct: number, total: number): number {
  return total <= 0 ? 0 : Math.min(1, Math.max(0, correct / total));
}

/** Память и звук вместе — «мнемоническая» сторона; без звука — одна память. */
export function mnemonicScore(scores: AptitudeScores): number {
  return scores.sound === null ? scores.memory : (scores.memory + scores.sound) / 2;
}

/** Всё высокое — от этой оценки. */
const HIGH = 0.75;
/** Ниже этого средняя оценка — нужен ведомый режим. */
const LOW = 0.45;
/** Разрыв, с которого одна сторона считается ведущей. */
const LEAD = 0.15;

/**
 * Профиль по оценкам. Порядок проверок важен: сначала крайние случаи
 * (всё высокое / всё низкое), затем ведущая сторона. Ровный средний
 * результат без ведущей стороны тоже уходит в ведомый режим — правило
 * первым и маленькие шаги надёжнее, когда сильной стороны не видно.
 */
export function classifyAptitude(scores: AptitudeScores): CognitiveProfile {
  const mnemonic = mnemonicScore(scores);
  const grammar = scores.grammar;
  if (mnemonic >= HIGH && grammar >= HIGH) return 'HIGH_APTITUDE';
  if ((mnemonic + grammar) / 2 < LOW) return 'LOW_APTITUDE';
  if (grammar - mnemonic >= LEAD) return 'ANALYTIC';
  if (mnemonic - grammar >= LEAD) return 'MNEMONIC';
  return 'LOW_APTITUDE';
}

/** Как идёт урок: порядок частей и «ведомый» режим. */
export interface LessonPlan {
  readonly strategy: Strategy;
  /** Маленькие шаги (дриллы по 3 с передышкой) и автоозвучка квокки. */
  readonly guided: boolean;
}

/** Насколько уверенно вес отдаётся ведущему маршруту. */
const LEAN = 0.75;

/** Веса маршрутов под стратегию: ведущий — 0.75, второй — 0.25. */
export function weightsFor(strategy: Strategy): StrategyWeights {
  return strategy === 'INDUCTIVE'
    ? { inductiveWeight: LEAN, deductiveWeight: 1 - LEAN }
    : { inductiveWeight: 1 - LEAN, deductiveWeight: LEAN };
}

/** Стартовый план урока по профилю. */
export function initialPlan(profile: CognitiveProfile, scores: AptitudeScores): LessonPlan {
  switch (profile) {
    case 'ANALYTIC':
      return { strategy: 'DEDUCTIVE', guided: false };
    case 'MNEMONIC':
      return { strategy: 'INDUCTIVE', guided: false };
    case 'HIGH_APTITUDE':
      // Обе стороны сильные — ведёт та, что сильнее; при равенстве — история.
      return { strategy: scores.grammar > mnemonicScore(scores) ? 'DEDUCTIVE' : 'INDUCTIVE', guided: false };
    case 'LOW_APTITUDE':
      return { strategy: 'DEDUCTIVE', guided: true };
  }
}

// Пересмотр плана по последним урокам — `adaptivePlan.ts`.
